import { useEffect, useRef, useState } from 'react';
import isDeepEqual from 'fast-deep-equal/react';
import type { ConditionValue, LogicValue } from '../../../configurator';
import type { FieldType, FormField } from '../../contexts';
import { checkValidCondition, compareNumericStrings } from '../utils';

export type ConditionalLogicEntity<T = unknown> = FormField & T;
export type ConditionalLogicChangedField = {
  value: string | number | string[] | undefined;
};
export type ConditionalLogicChangedFields = Record<
  string,
  ConditionalLogicChangedField
>;
type FieldID = string;
type FieldNode = {
  id: FieldID;
  dependsOn: FieldID[];
  children: FieldID[];
  conditions?: ConditionValue[];
  conditionalLogic?: LogicValue;
  hasConditionalLogic?: boolean;
};
type FieldGraph = Record<FieldID, FieldNode>;
type VisibilityMap = Record<FieldID, boolean>;

function buildGraph(fields: FormField[]): FieldGraph {
  const graph: FieldGraph = {};

  for (const field of fields) {
    graph[field.id] = {
      id: field.id,
      dependsOn: [],
      children: [],
      conditions: field.conditions,
      conditionalLogic: field.conditionalLogic,
      hasConditionalLogic: field.hasConditionalLogic
    };
  }

  for (const field of fields) {
    const dependencies =
      field.conditions?.map(({ fieldID }) => fieldID).filter(Boolean) || [];
    for (const dep of dependencies) {
      const cannotLinkDependency = !dep || !graph[field.id] || !graph[dep];
      if (cannotLinkDependency) {
        continue;
      }

      if (field.hasConditionalLogic) {
        graph[field.id].dependsOn.push(dep);
      }
      graph[dep].children.push(field.id);
    }
  }

  return graph;
}

function topologicalSort(graph: FieldGraph): FieldID[] {
  const visited = new Set<FieldID>();
  const visiting = new Set<FieldID>();
  const result: FieldID[] = [];
  const hasCycle = new Set<FieldID>();

  function visit(nodeId: FieldID) {
    if (hasCycle.has(nodeId) || visited.has(nodeId)) return;

    if (visiting.has(nodeId)) {
      hasCycle.add(nodeId);
      return;
    }

    visiting.add(nodeId);

    for (const dep of graph[nodeId].dependsOn) {
      visit(dep);
      if (hasCycle.has(dep)) {
        hasCycle.add(nodeId);
        return;
      }
    }

    visiting.delete(nodeId);
    visited.add(nodeId);
    result.push(nodeId);
  }

  Object.keys(graph).forEach(visit);

  if (hasCycle.size > 0) {
    console.error('[ConditionalLogic] Detected circular dependency.');
  }

  return result.filter((id) => !hasCycle.has(id));
}

function normalizePhone(phoneValue: string | number | string[] | undefined) {
  const normalizedValue = String(phoneValue).replace(/\D/g, '');

  return normalizedValue.length > 0
    ? '+' + String(phoneValue).replace(/\D/g, '')
    : normalizedValue;
}

function checkConditionMatch(
  field: ConditionalLogicChangedField,
  conditionLogic: ConditionValue,
  type?: FieldType
): boolean {
  const { condition, value } = conditionLogic;

  const rawValue = field?.value;
  const fieldValue = type === 'phone' ? normalizePhone(rawValue) : rawValue;

  const hasFieldValue =
    (Array.isArray(fieldValue) && fieldValue.length > 0) ||
    fieldValue === 0 ||
    !!fieldValue;

  if (!value) {
    if (condition === 'empty') return !hasFieldValue;
    if (condition === 'notEmpty') return hasFieldValue;
    return true;
  }

  const isEqual = Array.isArray(fieldValue)
    ? fieldValue.includes(value)
    : fieldValue === value;
  if (condition === 'is') return isEqual;
  if (condition === 'isNot') return !isEqual;

  const contains = () =>
    hasFieldValue &&
    (Array.isArray(fieldValue)
      ? fieldValue.some((item) => (item as string).includes(value))
      : (fieldValue as string).includes(value));
  if (condition === 'contain') return contains();
  if (condition === 'containNot') return !contains();

  if (condition === 'start') {
    return (
      hasFieldValue &&
      (Array.isArray(fieldValue)
        ? fieldValue.some((item) => (item as string).startsWith(value))
        : (fieldValue as string).startsWith(value))
    );
  }

  if (condition === 'end') {
    return (
      hasFieldValue &&
      (Array.isArray(fieldValue)
        ? fieldValue.some((item) => (item as string).endsWith(value))
        : (fieldValue as string).endsWith(value))
    );
  }

  if (condition === 'greater') {
    return (
      hasFieldValue && compareNumericStrings(fieldValue as string, value, true)
    );
  }

  if (condition === 'less') {
    return hasFieldValue && compareNumericStrings(fieldValue as string, value);
  }

  return true;
}

function evaluateFieldVisibility({
  node,
  changedFields,
  visibility,
  fieldTypeMap
}: {
  node: FieldNode;
  changedFields: ConditionalLogicChangedFields;
  visibility: VisibilityMap;
  fieldTypeMap: Record<string, FieldType | undefined>;
}): boolean {
  const { hasConditionalLogic, conditionalLogic, conditions } = node;

  if (!hasConditionalLogic || !conditionalLogic || !conditions?.length) {
    return true;
  }

  const { operator = 'AND', action } = conditionalLogic;

  const results = conditions.map((condition) => {
    const parentId = condition.fieldID!;
    const parentVisible = visibility[parentId];
    const type = fieldTypeMap[parentId];

    if (!parentVisible) return false;

    const parentField = changedFields[parentId];
    if (!parentField) return false;

    return checkConditionMatch(parentField, condition, type);
  });

  const isMet =
    operator === 'AND' ? results.every(Boolean) : results.some(Boolean);

  return (action === 'show' && isMet) || (action === 'hide' && !isMet);
}

function validateFieldConditions(fields: ConditionalLogicEntity[]) {
  return fields.map((field) => {
    const conditions = field.conditions?.filter((condition) =>
      checkValidCondition(condition, fields)
    );
    return { ...field, conditions };
  });
}

function calcConditionalFields<T>(
  fields: ConditionalLogicEntity<T>[],
  changedFields: ConditionalLogicChangedFields
) {
  const validatedFields = validateFieldConditions(
    fields as ConditionalLogicEntity[]
  );
  const fieldTypeMap = Object.fromEntries(
    validatedFields.map((field) => [field.id, field.type as FieldType])
  );
  const graph = buildGraph(validatedFields);
  const order = topologicalSort(graph);

  const visibility: VisibilityMap = {};
  for (const fieldId of order) {
    const node = graph[fieldId];

    visibility[fieldId] = evaluateFieldVisibility({
      node,
      visibility,
      changedFields,
      fieldTypeMap
    });
  }

  return fields.filter((field) => visibility[field.id] ?? false);
}

export function useConditionalLogic<T>(
  fields: ConditionalLogicEntity<T>[],
  changedFields: ConditionalLogicChangedFields
): ConditionalLogicEntity<T>[] {
  const fieldsRef = useRef(changedFields);

  const [conditionalFields, setConditionalFields] = useState(
    calcConditionalFields(fields, fieldsRef.current)
  );

  if (!isDeepEqual(fieldsRef.current, changedFields)) {
    fieldsRef.current = { ...changedFields };
  }

  useEffect(() => {
    const newConditionalFields = calcConditionalFields(
      fields,
      fieldsRef.current
    );

    setConditionalFields((previousConditionalFields) => {
      if (isDeepEqual(previousConditionalFields, newConditionalFields)) {
        return previousConditionalFields;
      }

      return newConditionalFields;
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [fields, fieldsRef.current]);

  return conditionalFields;
}
